import type { ReactElement } from 'react'
import { Link } from '@tanstack/react-router'
import type { ErrorComponentProps } from '@tanstack/react-router'
import { Button, Callout, Code, Spinner } from './ui/index.ts'

/**
 * What the app shows when a route is broken, and what it shows while a
 * route is still loading.
 *
 * Until now it showed neither. Every loader in this app throws on a failed
 * fetch (`api-fetch.ts` raises `ApiError`), nothing upstream caught it, and
 * React unmounted the tree — so a 500 from the control API, or a vault node
 * an operator is not granted, produced a white page with the real reason
 * only in the console. These two components are wired onto the root route
 * in `routes/__root.tsx` and as the router defaults in `router.tsx`, which
 * between them cover a throw at any depth.
 *
 * Neither renders the shell. The root route's error boundary sits OUTSIDE
 * the root component (TanStack nests shell > CatchBoundary > component), so
 * a root-level error has no chrome to sit in; a child-level error already
 * has the shell around it from the root component. Rendering `AppShell`
 * here would give the second case two sidebars.
 */

/** The message to show, with the raw one kept out of the way. An operator
 * needs the real text — "unknown environment \"prod\"" is the difference
 * between a typo and a missing grant — but it is a developer string, so it
 * goes in a `<Code>` block under a sentence that is not. */
function errorText(error: unknown): string {
  if (error instanceof Error && error.message !== '') return error.message
  return String(error)
}

export function RouteError({
  error,
  reset,
}: ErrorComponentProps): ReactElement {
  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-3 p-8">
      <Callout
        tone="danger"
        title="This page did not load"
        action={
          // `reset` re-runs the failed match rather than reloading the
          // document, so a transient 502 costs one click instead of a full
          // re-fetch of everything else on the page.
          <Button
            variant="danger"
            size="sm"
            onClick={() => {
              reset()
            }}
          >
            Retry
          </Button>
        }
      >
        The request behind it failed. Retry first; if it keeps failing, the
        message below is what the API actually said.
      </Callout>
      <Code isBlock isCopyable copyLabel="Copy error">
        {errorText(error)}
      </Code>
      <p className="text-xs text-muted">
        <Link to="/" className="text-accent hover:underline">
          Back to the start page
        </Link>
      </p>
    </div>
  )
}

/**
 * The gap between clicking a nav item and the loader resolving.
 *
 * A spinner and a word, nothing else. The brief's motion budget is 120ms on
 * colour and nothing more — no skeleton shimmer, because a skeleton that
 * pulses for the three seconds a cold Neon branch takes to answer is three
 * seconds of movement in the corner of an operator's eye for no
 * information. TanStack only mounts this after `defaultPendingMs`, so a
 * fast navigation never flashes it at all.
 *
 * Not built on `EmptyState`: its icon slot is wrapped `aria-hidden`, which
 * is right for a decorative glyph and wrong for a `Spinner`, whose whole
 * job is a `role="status"` a screen reader can hear. The spinner carries
 * the announcement; the visible word is for everyone else, and is hidden
 * from assistive tech so the state is not read out twice.
 */
export function RoutePending(): ReactElement {
  return (
    <div className="flex items-center gap-2 p-8 text-sm text-muted">
      <Spinner size="md" label="Loading" />
      <span aria-hidden="true">Loading…</span>
    </div>
  )
}
